import { useTranslation } from "react-i18next";

import { Tooltip } from "@/components/Tooltip/Tooltip";

import { formatCount, formatDate } from "../utils/featureUsage";

// A row is mounted only on the current table page. Null cells keep their place and a dashed
// outline; zero has a solid, empty cell. Every day is available by keyboard as well as hover.
export function UsageTimeline({
	days,
	values,
	label,
}: {
	days: readonly string[];
	values: readonly (number | null)[];
	label: string;
}) {
	const { t } = useTranslation();
	const max = Math.max(1, ...values.map((value) => value ?? 0));

	return (
		<div aria-label={label} className="min-w-56" role="group">
			<div className="flex h-9 items-end gap-px">
				{days.map((day, index) => {
					const value = values[index] ?? null;
					const text = `${label}, ${formatDate(day)} UTC: ${value === null ? t("featureUsage.notMeasured") : formatCount(value)}`;

					return (
						<Tooltip key={day} className="h-full min-w-0 flex-1" content={text}>
							<div
								aria-label={text}
								className={`flex h-full w-full items-end rounded-sm ${value === null ? "border border-dashed border-text-meta" : "bg-brand-light/10"}`}
								role="img"
								tabIndex={0}
							>
								{value !== null && (
									<div
										className="w-full bg-brand-light"
										style={{ height: `${(value / max) * 100}%` }}
									/>
								)}
							</div>
						</Tooltip>
					);
				})}
			</div>
			<div className="mt-1 flex justify-between text-xs text-text-meta">
				<span>{days[0] && formatDate(days[0])}</span>
				<span>{days.length > 1 && formatDate(days[days.length - 1])}</span>
			</div>
		</div>
	);
}
